// frontend/src/auth/roles.js
// Fuente única de verdad de roles y permisos del frontend. `user.rol` viaja
// en el JWT como el nombre plano del rol (sin prefijo ROLE_), ver AuthContext.js.

export const ROLES = {
    ADMIN: 'ADMIN',
    PROGRAMADOR: 'PROGRAMADOR',
    SUPERVISOR: 'SUPERVISOR',
    ART: 'ART',
    RTI: 'RTI',
    RECEPCION: 'RECEPCION',
};

// Permisos como capacidades, no como secciones de menú: así el menú, los guards
// de ruta y los botones de acción consultan lo mismo. '*' matchea cualquier permiso.
export const PERMISOS = {
    // El '*' NO cubre los permisos de PERMISOS_EXCLUSIVOS (ver abajo). Los listados ya salieron de
    // esa lista (oct-2026, visibles para todos los roles), igual que antes cobranzas.
    [ROLES.ADMIN]: ['*'],
    // 'encuesta.resultados' también va listado aparte por ser exclusivo: los resultados de la
    // encuesta de satisfacción los mira sólo PROGRAMADOR (ver PERMISOS_EXCLUSIVOS abajo).
    [ROLES.PROGRAMADOR]: ['*', 'encuesta.resultados'],
    [ROLES.SUPERVISOR]: [
        // Listados (A liquidar, Para aprobar, Favorables, Altas, Deudores): visibles para todos.
        'listados.ver',
        'clientes.ver', 'clientes.editar',
        'tramites.ver', 'tramites.editar',
        'general.ver', 'agenda.ver',
        'recepcion.ver',
        'reportes.ver',
        'herramientas.ver', 'herramientas.expedientes',
        'datosMaestros.ver', 'datosMaestros.editar',
        'turnero.config',
        'cobranzas.cobrar',
    ],
    [ROLES.ART]: [
        // Listados (A liquidar, Para aprobar, Favorables, Altas, Deudores): visibles para todos.
        'listados.ver',
        'clientes.ver', 'clientes.editar',
        'tramites.ver', 'tramites.editar',
        'art.ver', 'art.editar',
        'general.ver', 'agenda.ver',
        'recepcion.ver',
        'reportes.ver',
        'herramientas.ver', 'herramientas.expedientes',
        'datosMaestros.ver', 'datosMaestros.editar',
        'correoExterno.ver',
        // Cobros (16-oct-2026): cualquier usuario registra pagos y reprograma cuotas.
        'cobranzas.cobrar',
    ],
    [ROLES.RTI]: [
        // Listados (A liquidar, Para aprobar, Favorables, Altas, Deudores): visibles para todos.
        'listados.ver',
        'clientes.ver', 'clientes.editar',
        'tramites.ver', 'tramites.editar',
        'rti.ver', 'rti.editar',
        'general.ver', 'agenda.ver',
        'recepcion.ver',
        'reportes.ver',
        'herramientas.ver', 'herramientas.expedientes',
        'datosMaestros.ver', 'datosMaestros.editar',
        'correoExterno.ver',
        // Cobros (16-oct-2026): cualquier usuario registra pagos y reprograma cuotas.
        'cobranzas.cobrar',
    ],
    [ROLES.RECEPCION]: [
        // Listados (A liquidar, Para aprobar, Favorables, Altas, Deudores): visibles para todos.
        'listados.ver',
        'clientes.ver', 'clientes.editar',
        'tramites.ver', 'tramites.editar',
        'recepcion.ver', 'agenda.ver',
        'herramientas.ver', 'herramientas.expedientes',
        'turnero.config',
        // Acceso acotado a Datos Maestros: a diferencia de 'datosMaestros.ver'/'editar' (que abren
        // TODAS las pestañas del módulo), esto solo habilita la pestaña Carpetas. RECEPCION ya crea
        // carpetas al vuelo desde la ficha del trámite (PanelUbicacionTramite); esto le da además el
        // ABM completo (ver auth/PermisoRoute.js y config/menuSections.js).
        'datosMaestros.carpetas.ver', 'datosMaestros.carpetas.editar',
        // Cobrar en el mostrador durante la atención de un turno de cobro. Es más chico que
        // 'cobranzas.editar' a propósito: ese gatea la configuración del cobro ya nacido (registrar
        // el alta, darlo de baja). El pacto de la ficha del trámite, antes del favorable, lo edita
        // cualquiera (ver TarjetaCobroTramite).
        // Espejo de Roles.COBRANZAS_OPERATIVO.
        'cobranzas.cobrar',
    ],
};

// Nombre legible de cada rol, para mostrar en la UI (navbar, Mi Perfil, etc.).
export const ROL_LABELS = {
    [ROLES.ADMIN]: 'Administrador',
    [ROLES.PROGRAMADOR]: 'Programador',
    [ROLES.SUPERVISOR]: 'Supervisor',
    [ROLES.ART]: 'ART',
    [ROLES.RTI]: 'RTI',
    [ROLES.RECEPCION]: 'Recepción',
};

export const etiquetaRol = (rol) => ROL_LABELS[rol] || 'Usuario';

// Permisos que el comodín '*' NO cubre: hay que listarlos explícitamente en el rol.
//
// Sirve para features en prueba que no queremos mostrarle ni siquiera a ADMIN. Sin esto no había
// forma de ocultarle algo a ADMIN, porque su '*' matchea cualquier permiso: la única alternativa
// habría sido reemplazar su '*' por la lista completa de permisos y mantenerla a mano.
//
// Al sacar una feature de prueba, se la borra de acá y vuelve a comportarse como cualquier otra.
// 'cobranzas.editar' ya salió de esta lista: ahora la ven ADMIN y PROGRAMADOR (vía '*'), quedó
// afuera únicamente de SUPERVISOR/ART/RTI/RECEPCION porque ninguno de esos roles la tiene listada.
// 'encuesta.resultados': la encuesta de satisfacción es anónima y la responde el personal sobre el
// sistema con el que trabaja. Los resultados los lee sólo PROGRAMADOR, que es quien prioriza las
// mejoras; por eso va acá y no alcanza con el '*' de ADMIN. El backend hace lo mismo con
// hasAuthority(Roles.PROGRAMADOR) en SecurityConfig: si se cambia uno hay que cambiar el otro,
// porque este permiso sólo oculta la pantalla, no protege el dato.
export const PERMISOS_EXCLUSIVOS = ['encuesta.resultados'];

// Chequea si un rol tiene un permiso dado. '*' matchea todo, salvo los exclusivos.
export const tienePermiso = (rol, permiso) => {
    if (!rol || !permiso) return false;
    const permisosDelRol = PERMISOS[rol];
    if (!permisosDelRol) return false;
    if (PERMISOS_EXCLUSIVOS.includes(permiso)) return permisosDelRol.includes(permiso);
    return permisosDelRol.includes('*') || permisosDelRol.includes(permiso);
};
